
import Vue from 'vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { db, functions } from '@/firebase';
import { loadStripe } from '@stripe/stripe-js';
import LoadingSVG from '@/assets/LoadingSVG.vue';
import {
	Team,
	User,
	STRIPE_PUBLISHABLE_KEY,
	CHECKOUT_RUNNIT_ONE_TIME_TOP_UP_TOKENS,
	CHECKOUT_RUNNIT_ONE_TIME_TOP_UP_TOKENS_TEAM,
	CHECKOUT_ADD_FUNDS,
	CHECKOUT_ADD_FUNDS_TEAM,
	CHECKOUT_USER_SUBSCRIPTION,
	CHECKOUT_TEAM_PLAN_SUBSCRIPTION,
	STRIPE_TEAMS_PUBLISHABLE_KEY,
	ClubKey,
	mapTrackingPurchaseRevenueItems,
} from '@run-diffusion/shared';
import ConfirmPlanChangeDialog from '@/views/GoToPayment/ConfirmPlanChangeDialog.vue';
import { getCheckoutPromotionCodes, trackGtmPurchase } from '@/utils';
import { getDomainClaimRestrictionMessage } from '@/utils/domainClaimRestriction';
import { analytics, EVENTS } from '@/services/analytics';

export default Vue.extend({
	name: 'GoToPayment',
	metaInfo () {
		let title = 'Payment | RunDiffusion';

		const checkoutAction = this._get(this.$route.query, 'checkoutAction', null);
		if ([CHECKOUT_RUNNIT_ONE_TIME_TOP_UP_TOKENS, CHECKOUT_RUNNIT_ONE_TIME_TOP_UP_TOKENS_TEAM].includes(checkoutAction)) {
			title = 'Purchase Tokens | RunDiffusion';
		} else if ([CHECKOUT_ADD_FUNDS, CHECKOUT_ADD_FUNDS_TEAM].includes(checkoutAction)) {
			title = 'Add Funds | RunDiffusion';
		} else if (checkoutAction === CHECKOUT_USER_SUBSCRIPTION) {
			title = 'Subscription Payment | RunDiffusion';
		} else if (checkoutAction === CHECKOUT_TEAM_PLAN_SUBSCRIPTION) {
			title = 'Team Plan Payment | RunDiffusion';
		}

		return { title };
	},
	data () {
		return {
			loadingCheckOutPage: true,

			confirmPlanChangeDialogConfig: {
				open: false,
				loading: false,
				isPaymentFailed: false,
				paymentFailedData: null,
				isUpdateNotAllowed: false,
				updateNotAllowedData: null,
				isAnnual: false,
				previewData: null,
				club: null,
			},

			loadingPageText: '',
			successPageHeader: '',
			successPageText: '',
			dedupConversionUuid: null,
			hasInitPage: false,
			// goToCheckoutPage re-runs on user/team watchers; these guard against
			// firing the analytics checkout events more than once per page load.
			hasTrackedCheckoutReturn: false,
			hasTrackedCheckoutStarted: false,
		};
	},
	computed: {
		...mapGetters([
			'displayLabels',
		]),
		// Top-up pack units picked in the token dialog (clamped server-side)
		requestedTopUpQty (): number {
			const parsed: number = parseInt(this._get(this.$route.query, 'qty', ''), 10);
			return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
		},
		...mapState([
			'user',
			'team',
			'deviceReportLocal',
		]),
	},
	async created () {
		await this.goToCheckoutPage(this.user, this.team, false);
		setTimeout(() => {
			this.loadingPageText = 'This is taking a while... Please try again by refreshing or closing this window. You can reach out to report issues by going to Resources -> Help from the menu options.';
		}, 15000);
	},
	watch: {
		async user (newVal: User) {
			await this.goToCheckoutPage(newVal, this.team, false);
		},
		async team (newVal: Team) {
			await this.goToCheckoutPage(this.user, newVal, false);
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		async onConfirmPlanChange () {
			try {
				this.confirmPlanChangeDialogConfig = {
					...this.confirmPlanChangeDialogConfig,
					loading: true,
				};
				await this.goToCheckoutPage(this.user, this.team, true);
			} catch (e) {
				console.error(e);
			} finally {
				this.confirmPlanChangeDialogConfig = {
					...this.confirmPlanChangeDialogConfig,
					loading: false,
				};
			}
		},
		async goToCheckoutPage (user: User, team: Team, doSubscriptionUpgrade: boolean) {
			const checkoutAction: string = this._get(this.$route.query, 'checkoutAction') || null;
			const returnAction: string = this._get(this.$route.query, 'returnAction') || null;
			const club: ClubKey = this._get(this.$route.query, 'club') || null;
			const hasClubQueryVariable: boolean = !!club;
			const hasReturnActionQueryVariable: boolean = !!returnAction;
			const isSuccessReturnAction: boolean = returnAction === 'success';
			const isCancelReturnAction: boolean = returnAction === 'cancel';
			const isRunnitOneTimeTopUpTokensCheckout: boolean = checkoutAction === CHECKOUT_RUNNIT_ONE_TIME_TOP_UP_TOKENS;
			const isTeamRunnitOneTimeTopUpTokensCheckout: boolean = checkoutAction === CHECKOUT_RUNNIT_ONE_TIME_TOP_UP_TOKENS_TEAM;
			const isAddFundsCheckout: boolean = checkoutAction === CHECKOUT_ADD_FUNDS;
			const isTeamAddFundsCheckout: boolean = checkoutAction === CHECKOUT_ADD_FUNDS_TEAM;
			const isUserSubscriptionCheckout: boolean = checkoutAction === CHECKOUT_USER_SUBSCRIPTION;
			const isTeamSubscriptionCheckout: boolean = checkoutAction === CHECKOUT_TEAM_PLAN_SUBSCRIPTION;
			const isTeamCheckout: boolean = isTeamRunnitOneTimeTopUpTokensCheckout || isTeamAddFundsCheckout || isTeamSubscriptionCheckout;
			const teamForCheckout: Team = (isTeamCheckout && team) || null;
			const restriction = this.getDomainRestrictionForCheckoutContext(teamForCheckout);

			if (hasReturnActionQueryVariable) {
				if (!this.hasTrackedCheckoutReturn) {
					this.hasTrackedCheckoutReturn = true;
					// Client-side proxy for the Stripe-hosted outcome. True payment
					// confirmation is webhook-driven on the backend.
					analytics.track(
						isSuccessReturnAction ? EVENTS.CHECKOUT_RETURNED_SUCCESS : EVENTS.CHECKOUT_RETURNED_CANCEL,
						{
							checkout_action: checkoutAction,
							club,
							is_team: isTeamCheckout,
						},
					);
				}
				if (isSuccessReturnAction) {
					await this.sendFrontendTrackingPurchaseMetrics(teamForCheckout);

					// Show success page
					this.loadingCheckOutPage = false;
					if (isRunnitOneTimeTopUpTokensCheckout || isTeamRunnitOneTimeTopUpTokensCheckout) {
						this.successPageHeader = 'Tokens have been added successfully!';
						this.successPageText = 'You can now close this tab and your tokens will be available.';
					} else if (isAddFundsCheckout || isTeamAddFundsCheckout) {
						this.successPageHeader = 'Funds have been added successfully!';
						this.successPageText = 'You can now close this tab and your funds will be available.';
					} else if (isUserSubscriptionCheckout) {
						this.successPageHeader = `Plan purchased successfully!`;
						this.successPageText = `You can now close this tab. Your subscription and benefits will be active. Please check your email inbox for documentation regarding your new membership that was just sent to you.`;
					} else if (isTeamSubscriptionCheckout) {
						this.successPageHeader = `Team Plan purchased successfully!`;
						this.successPageText = `You can now close this tab. Your subscription and benefits will be active. Please check your email inbox for next steps.`;
					}
				} else if (isCancelReturnAction) {
					this.handleWindowClose();
				}
				return;
			}

			if (
				(!doSubscriptionUpgrade && this.hasInitPage) ||
				(isRunnitOneTimeTopUpTokensCheckout && !user) ||
				(isTeamRunnitOneTimeTopUpTokensCheckout && (!team || !user)) ||
				(isAddFundsCheckout && !user) ||
				(isTeamAddFundsCheckout && (!team || !user)) ||
				(isUserSubscriptionCheckout && (!user || !hasClubQueryVariable)) ||
				(isTeamSubscriptionCheckout && (!team || !user))
			) {
				return;
			}
			if (restriction) {
				this.loadingCheckOutPage = false;
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: getDomainClaimRestrictionMessage(restriction, this.displayLabels.team),
					show: true,
				});
				this.successPageHeader = 'Action blocked';
				this.successPageText = getDomainClaimRestrictionMessage(restriction, this.displayLabels.team);
				return;
			}

			this.hasInitPage = true;

			if (user) {
				if (!this.hasTrackedCheckoutStarted) {
					this.hasTrackedCheckoutStarted = true;
					// User is being sent to the Stripe-hosted checkout.
					analytics.track(EVENTS.CHECKOUT_STARTED, {
						checkout_action: checkoutAction,
						club,
						is_team: isTeamCheckout,
						is_subscription_upgrade: doSubscriptionUpgrade,
					});
				}

				// FirstPromoter attribution
				if (window.fpr) {
					window.fpr('referral', { email: user.email });
				}

				if (
					isRunnitOneTimeTopUpTokensCheckout ||
					isTeamRunnitOneTimeTopUpTokensCheckout ||
					isAddFundsCheckout ||
					isTeamAddFundsCheckout
				) {
					this.loadingPageText = 'Loading payment page...';
					await this.goToAddFundsCheckOutPage(
						isRunnitOneTimeTopUpTokensCheckout || isTeamRunnitOneTimeTopUpTokensCheckout
							? 'runnit'
							: 'rd',
						user,
						teamForCheckout,
					);
				} else if (
					isUserSubscriptionCheckout ||
					isTeamSubscriptionCheckout
				) {
					this.loadingPageText = 'Loading subscription page...';
					await this.goToBuySubscriptionPage(teamForCheckout, doSubscriptionUpgrade);
				}
			}
		},
		handleWindowClose () {
			console.log('handleWindowClose');
			window.close();
		},
		async sendFrontendTrackingPurchaseMetrics (teamForCheckout: Team) {
			try {
				const conversionUuid: string = this._get(this.$route.query, 'conversionUuid') || null;

				// Prevent conversion from tracking twice
				if (!conversionUuid || this.dedupConversionUuid) return;
				this.dedupConversionUuid = conversionUuid;
				const newQuery = { ...this.$route.query };
				delete newQuery.conversionUuid;
				this.routerReplace(this.$route, this.$router, { query: newQuery });

				const functionRef = functions
					.httpsCallable('getFrontendTrackingPurchaseMetrics');
				const { paymentId, payment } = (await functionRef({
					conversionUuid,
					...(teamForCheckout && {
						teamId: teamForCheckout.id,
					}),
				})).data;
				if (paymentId && payment) {
					const { mappedItems } = mapTrackingPurchaseRevenueItems(payment);
					trackGtmPurchase(paymentId, payment.amount, payment.currency, mappedItems);
				}
			} catch (e) {
				console.error(e);
				// ignore error
			}
		},
		async goToAddFundsCheckOutPage (platform: 'rd' | 'runnit', userForCheckout: User, teamForCheckout: Team) {
			const onError: Function = (e: any) => {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error loading payment page, please reach out to report issues by going to Resources -> Help from the menu options',
					show: true,
				});
			};

			const checkoutAction: string = this._get(this.$route.query, 'checkoutAction') || null;
			try {
				// Get tracking context
				const pageUri: string = window.location.href;
				const pageName: string = teamForCheckout ? 'TeamPaymentSucceeded' : 'PersonalOneOffPaymentSucceeded';

				if (teamForCheckout) {
					const publishableKey: string = STRIPE_TEAMS_PUBLISHABLE_KEY;

					const checkoutDocRef: any = db
						.collection(`stripeTeamsCustomers/${teamForCheckout.id}/checkout_sessions`)
						.doc();

					const functionRef = functions
						.httpsCallable('newAddFundsTeamsCheckout');
					const { error, checkoutSessionId } = (await functionRef({
						teamId: teamForCheckout.id,
						checkoutDocId: checkoutDocRef.id,
						checkoutAction,
						...(this.requestedTopUpQty && { qty: this.requestedTopUpQty }),
						// tracking context
						pageUri,
						pageName,
					})).data;

				await this.processCheckoutData(error, checkoutSessionId, publishableKey, onError);
			} else {
				const publishableKey: string = STRIPE_PUBLISHABLE_KEY;

				const checkoutDocRef: any = db
					.collection(`stripeCustomers/${userForCheckout.id}/checkout_sessions`)
					.doc();

				// Wait for the checkoutSession to get attached by the extension
				checkoutDocRef.onSnapshot(async (checkoutSnapshot) => {
					const { error, sessionId } = checkoutSnapshot.data();
					await this.processCheckoutData(error, sessionId, publishableKey, onError);
				});

					const functionRef = functions
						.httpsCallable('newAddFundsCheckout');
					const { success } = (await functionRef({
						checkoutDocId: checkoutDocRef.id,
						checkoutAction,
						...(this.requestedTopUpQty && { qty: this.requestedTopUpQty }),
						// tracking context
						pageUri,
						pageName,
					})).data;
					if (!success) onError(new Error('newAddFundsCheckout returned not success'));
				}
			} catch (e) {
				onError(e);
			}
		},
		async goToBuySubscriptionPage (teamForCheckout: Team, doSubscriptionUpgrade: boolean) {
			const isAnnual: boolean = !!this._get(this.$route.query, 'annual', null);
			const club: ClubKey = this._get(this.$route.query, 'club', null);
			const tokenClubAddOnId: string = this._get(this.$route.query, 'tokenClubAddOnId', null);
			const limitClubAddOnId: string = this._get(this.$route.query, 'limitClubAddOnId', null);

			const onError: Function = (e: any) => {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error loading subscription page, please reach out to report issues by going to Resources -> Help from the menu options',
					show: true,
				});
			};

			try {
				// Get tracking context
				const pageUri: string = window.location.href;
				const pageName: string = teamForCheckout ? 'TeamPaymentSucceeded' : 'PersonalSubscriptionPaid';

				if (teamForCheckout) {
					const publishableKey: string = STRIPE_TEAMS_PUBLISHABLE_KEY;

					const checkoutDocRef: any = db
						.collection(`stripeTeamsCustomers/${teamForCheckout.id}/checkout_sessions`)
						.doc();

					const functionRef = functions
						.httpsCallable('newSubscriptionTeamsCheckout');
					const { error, checkoutSessionId } = (await functionRef({
						teamId: teamForCheckout.id,
						checkoutDocId: checkoutDocRef.id,
						isAnnual,
						// tracking context
						pageUri,
						pageName,
					})).data;

					await this.processCheckoutData(error, checkoutSessionId, publishableKey, onError);
				} else {
					this.confirmPlanChangeDialogConfig = {
						...this.confirmPlanChangeDialogConfig,
						isPaymentFailed: false,
						paymentFailedData: null,
						isUpdateNotAllowed: false,
						updateNotAllowedData: null,
					};

					const publishableKey: string = STRIPE_PUBLISHABLE_KEY;

					const checkoutPromotionCodes: Record<ClubKey, string> = getCheckoutPromotionCodes();
					const promotionCode: string = checkoutPromotionCodes[club] || (checkoutPromotionCodes as any).ALL || null;
					const functionRef = functions
						.httpsCallable('getSubscriptionCheckout');
					const {
						success,
						checkoutData,
						previewData,
						paymentFailedData,
						updateNotAllowedData,
					} = (await functionRef({
						...(promotionCode && { promotionCode }),
						doUpgrade: !!doSubscriptionUpgrade,
						isAnnual,
						club,
						tokenClubAddOnId,
						limitClubAddOnId,
						// tracking context
						pageUri,
						pageName,
					})).data;
					if (success && previewData) {
						this.confirmPlanChangeDialogConfig = {
							open: true,
							loading: false,
							isPaymentFailed: false,
							paymentFailedData: null,
							isUpdateNotAllowed: false,
							updateNotAllowedData: null,
							isAnnual,
							previewData,
							club,
						};
					} else if (success && checkoutData) {
						await this.processCheckoutData(null, checkoutData.id, publishableKey, onError);
					} else if (!success && paymentFailedData) {
						this.confirmPlanChangeDialogConfig = {
							...this.confirmPlanChangeDialogConfig,
							open: true,
							isPaymentFailed: true,
							paymentFailedData,
							isUpdateNotAllowed: false,
							updateNotAllowedData: null,
						};
					} else if (!success && updateNotAllowedData) {
						this.confirmPlanChangeDialogConfig = {
							...this.confirmPlanChangeDialogConfig,
							open: true,
							isPaymentFailed: false,
							paymentFailedData: null,
							isUpdateNotAllowed: true,
							updateNotAllowedData,
						};
					} else if (success && doSubscriptionUpgrade) {
						this.confirmPlanChangeDialogConfig = {
							...this.confirmPlanChangeDialogConfig,
							open: false,
							loading: false,
							isPaymentFailed: false,
							paymentFailedData: null,
							isUpdateNotAllowed: false,
							updateNotAllowedData: null,
							isAnnual: false,
							previewData: null,
							club: null,
						};
						this.loadingCheckOutPage = false;
						this.successPageHeader = `Plan updated successfully!`;
						this.successPageText = `You can now close this tab. Your subscription and benefits will be active. Please check your email inbox for any documentation regarding your new membership that may have been sent to you.`;
					} else {
						onError(new Error('getSubscriptionCheckout returned not success'));
					}
				}
			} catch (e) {
				onError(e);
			}
		},
		async processCheckoutData (error: string, sessionId: string, publishableKey: string, onError: Function) {
			try {
				if (error) {
					onError(error);
				}

				if (sessionId) {
					// We have a checkout session, let's redirect to Checkout screen
					// Init Stripe
					const stripe = await loadStripe(publishableKey);
					await stripe.redirectToCheckout({ sessionId });
				}
			} catch (e) {
				onError(e);
			}
		},
		getDomainRestrictionForCheckoutContext (teamForCheckout: Team | null) {
			const restriction = this._get(this.deviceReportLocal, 'domainClaimRestriction') || null;
			if (!restriction) {
				return null;
			}
			if (teamForCheckout && teamForCheckout.companyId === restriction.companyId) {
				return null;
			}
			return restriction;
		},
	},
	components: {
		ConfirmPlanChangeDialog,
		LoadingSVG,
	},
});
